import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

/**
 * Lot lifecycle: OPEN → DEPOSITED → CONFIRMED (or DISCREPANCY).
 * CANCELLED: every receipt in the lot was rejected (cancelled) one by one.
 * REJECTED: a manager sent a DEPOSITED or DISCREPANCY lot back whole; its
 * receipts went back to the agent uncancelled, to resubmit in a new lot.
 */
export type LotStatus = 'OPEN' | 'DEPOSITED' | 'CONFIRMED' | 'DISCREPANCY' | 'CANCELLED' | 'REJECTED'

export interface LotRow {
  lotId: string
  agentUserId: string
  status: LotStatus
  /** Amount in INR. String to preserve precision over the wire. */
  reportedAmount: string
  confirmedAmount: string | null
  depositReference: string | null
  depositedAt: string | null
  confirmedAt: string | null
  confirmedByUserId: string | null
  discrepancyNote: string | null
  openedAt: string
  /** Which of the lender's accounts the agent deposited into (V35). The label
   *  is a snapshot taken at deposit time, so renaming the account later does
   *  not rewrite where old cash went. Null on lots deposited before V35. */
  depositBankAccountId?: string | null
  depositBankLabel?: string | null
  /** reportedAmount drops as receipts are rejected (a fully rejected lot
   *  reads 0); this is what the agent reported when opening the lot. */
  originalReportedAmount?: string | null
  /** When the last receipt was rejected and the lot became CANCELLED. */
  cancelledAt?: string | null
  /** Who rejected the last receipt, cancelling the lot. */
  cancelledByUserId?: string | null
  /** The agent uploaded a deposit-slip photo with the deposit. */
  hasDepositSlip?: boolean
  /** Set when a manager rejected the whole lot (status REJECTED). */
  rejectedAt?: string | null
  rejectedByUserId?: string | null
  rejectionReason?: string | null
}

export interface ConfirmLotResponse {
  lotId: string
  newStatus: LotStatus
  amountsMatched: boolean
  reportedAmount: string
  confirmedAmount: string
}

/**
 * The confirmer's worklist. `/lots/to-confirm`, not `/lots`: the plain list is
 * gated on RECEIPT_VIEW, which Branch Managers do not hold, and this one is
 * scoped server-side to lots deposited by agents inside the caller's branch or
 * area.
 */
export function useLots(status: LotStatus, limit = 100) {
  return useQuery({
    queryKey: ['cash', 'lots', status, limit],
    queryFn: async () => {
      const res = await api.get<LotRow[]>('/v1/cash/lots/to-confirm', { params: { status, limit } })
      return res.data
    },
    refetchInterval: 30_000,
  })
}

export interface ConfirmLotInput {
  lotId: string
  confirmedAmount: number
  note?: string
}

export function useConfirmLot() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ lotId, confirmedAmount, note }: ConfirmLotInput) => {
      const res = await api.patch<ConfirmLotResponse>(`/v1/cash/lots/${lotId}/confirm`, {
        confirmedAmount,
        note: note?.trim() ? note.trim() : undefined,
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cash', 'lots'] })
    },
  })
}

export interface ReceiptInLot {
  receiptId: string
  receiptSeq: number
  caseId: string | null
  loanId: string
  /** The loan account number the confirmer reads the receipt against. */
  loanAccountNo: string | null
  customerId: string | null
  agentUserId: string
  lotId: string
  amount: string
  currency: string
  /** Channel type code (CASH, UPI, CHEQUE, …). Not a closed union: the list
   *  is fixed server-side but grew past the original six, and the only
   *  consumer labels it through `label('paymentMode', …)`. */
  paymentMode: string
  collectedAt: string
  recordedAt: string
  requiresManagerReview: boolean
  cancelledAt: string | null
  cancelledReason?: string | null
  cancelledByUserId?: string | null
  notes: string | null
}

/**
 * Receipts inside one lot. Used inside the confirm drawer so branch ops
 * sees the exact list they're accepting before they enter the amount —
 * a discrepancy on a single receipt is much easier to spot here than
 * after the lot lands in DISCREPANCY status.
 */
export function useReceiptsInLot(lotId: string | null) {
  return useQuery({
    queryKey: ['cash', 'lot-receipts', lotId],
    enabled: !!lotId,
    queryFn: async () => {
      const res = await api.get<ReceiptInLot[]>(`/v1/cash/lots/${lotId}/receipts`, {
        params: { limit: 500 },
      })
      return res.data
    },
  })
}

export interface DepositSlip {
  blob: Blob
  /** False when the stored slip no longer hashes to what was recorded at upload. */
  verified: boolean
}

/**
 * The bank deposit slip the agent photographed, for the confirmer to read the
 * amount off. Fetched through axios rather than an `<img src>` because the
 * endpoint is authenticated and an image tag sends no bearer token.
 */
export function useDepositSlip(lotId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['cash', 'lot-slip', lotId],
    enabled: !!lotId && enabled,
    queryFn: async (): Promise<DepositSlip> => {
      const res = await api.get<Blob>(`/v1/cash/lots/${lotId}/deposit-slip`, { responseType: 'blob' })
      return { blob: res.data, verified: res.headers['x-slip-sha256-verified'] !== 'false' }
    },
    // The slip never changes once deposited; a 403/404 will not heal on retry.
    staleTime: Infinity,
    retry: false,
  })
}

// ---------------------------------------------------------------------------
// Cash limit requests (V35)
// ---------------------------------------------------------------------------

/**
 * An agent who hit the cash limit in the field asking for room. They cannot
 * approve their own way past it and the manager is not standing next to them,
 * so the ask lands here. Approving raises that agent's limit; their blocked
 * capture then simply succeeds on retry.
 */
export interface CashLimitRequest {
  requestId: string
  agentUserId: string
  agentEmpId: string
  agentName: string
  /** INR as strings, to preserve precision over the wire. */
  requestedLimitInr: string
  exposureAtRequest: string
  blockedAmount: string | null
  reason: string
  status: 'REQUESTED' | 'APPROVED' | 'REJECTED'
  decidedByEmpId: string | null
  decidedAt: string | null
  decisionReason: string | null
  grantedUntil: string | null
  requestedAt: string
}

export function useCashLimitRequests(status: 'open' | 'decided' = 'open', limit = 50) {
  return useQuery({
    queryKey: ['cash', 'limit-requests', status, limit],
    queryFn: async () => {
      const res = await api.get<CashLimitRequest[]>('/v1/cash/limit-requests', {
        params: { status, limit },
      })
      return res.data
    },
    refetchInterval: 30_000,
    // A team lead reaching this page holds CASH_DEPOSIT_CONFIRM but not
    // CASH_CEILING_OVERRIDE; a 403 hides the card rather than retrying.
    retry: false,
  })
}

export interface DecideCashLimitInput {
  requestId: string
  decision: 'approve' | 'reject'
  reason?: string
  /** When the raised limit lapses. Omitted leaves it until a manager changes it. */
  grantedUntil?: string
}

export function useDecideCashLimit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ requestId, decision, reason, grantedUntil }: DecideCashLimitInput) => {
      const res = await api.post<CashLimitRequest>(
        `/v1/cash/limit-requests/${requestId}/${decision}`,
        { reason: reason?.trim() ? reason.trim() : undefined, grantedUntil },
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cash', 'limit-requests'] })
    },
  })
}

/**
 * Send a whole DEPOSITED or DISCREPANCY lot back to its agent. Nothing is
 * cancelled: the receipts return to the agent to resubmit in a new lot.
 */
export function useRejectLot() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ lotId, reason }: { lotId: string; reason: string }) =>
      (await api.post(`/v1/cash/lots/${lotId}/reject`, { reason: reason.trim() })).data,
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['cash'] }),
        qc.invalidateQueries({ queryKey: ['receipts'] }),
        qc.invalidateQueries({ queryKey: ['collections'] }),
      ])
    },
  })
}

/**
 * Rejected lots that held any of this lot's receipts: the earlier deposit
 * behind a resubmission, so the confirmer can add its slip to this one's.
 */
export function usePreviousLots(lotId: string | null) {
  return useQuery({
    queryKey: ['cash', 'lot-previous', lotId],
    enabled: !!lotId,
    queryFn: async () => (await api.get<LotRow[]>(`/v1/cash/lots/${lotId}/previous-lots`)).data,
    retry: false,
  })
}

export function useRejectReceipt() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ receiptId, reason }: { receiptId: string; reason: string }) =>
      (await api.post(`/v1/cash/receipts/${receiptId}/reject`, { reason: reason.trim() })).data,
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['cash'] }),
        qc.invalidateQueries({ queryKey: ['receipts'] }),
        qc.invalidateQueries({ queryKey: ['collections'] }),
      ])
    },
  })
}
